<script setup lang="ts">
import { computed, ref } from "vue";
import { useUserStore } from "../../stores/user";
import { useQueryStore } from "../../stores/query";
import {
  getChildAges,
  parseDefaultDate,
  getStringParam,
} from "../../utils/searchParams";
import { getDefaultCabinClass } from "../../utils/cabinClass";
import { convertToDate, nightBetweenDates } from "../../utils/dateUtils";
import { numberParser } from "../../utils/numbers";
import BaseSearchForm from "../SearchForm/BaseSearchForm.vue";
import type { Entity } from "../LocationField/adapter";
import { GuestsData, intParser } from "vacv-components";
import type { ProductType } from "../../../types/common";
import { ModifySearchMode } from "../../../types/common";
import { TabConfig } from "../../../types/tabs";

export interface Props {
  tabConfig: TabConfig;
  productType: ProductType;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  "search-mode": [mode: ModifySearchMode];
}>();

const MIN_STAY = 3;
const tabConfig = computed(() => {
  return {
    ...props.tabConfig,
    minStay: MIN_STAY,
  };
});

const userStore = useUserStore();
const queryStore = useQueryStore(props.tabConfig.id!);

const searchParams = new URLSearchParams(window.location.search);

function createEntityFromSearchParams(prefix: string, name: string): Entity {
  return {
    destinationId: "",
    name,
    code: getStringParam(searchParams, `${prefix}PlaceId`, prefix),
    latitude: numberParser(searchParams.get(`${prefix}Latitude`)),
    longitude: numberParser(searchParams.get(`${prefix}Longitude`)),
    tts: false,
    type: searchParams.get(`${prefix}SuggestionType`),
    useSPABookingFlow: false,
    cubaDestination:
      prefix === "arrivalLocation" &&
      searchParams.get("cubaDestination") === "true",
    ttsDestinationCode:
      prefix === "arrivalLocation"
        ? searchParams.get("dest_dep")?.toString()
        : undefined,
    ttsHotelCode:
      prefix === "arrivalLocation"
        ? getStringParam(searchParams, "", "no_hotel")
        : undefined,
  } as Entity;
}

const departureLocationName = searchParams.get("departureLocationName") ?? "";
const departureEntity = props.tabConfig.isModifySearch
  ? createEntityFromSearchParams("departureLocation", departureLocationName)
  : null;
const arrivalLocationName = searchParams.get("arrivalLocationName") ?? "";
const arrivalEntity = props.tabConfig.isModifySearch
  ? createEntityFromSearchParams("arrivalLocation", arrivalLocationName)
  : null;
const departureDate = parseDefaultDate(searchParams, "departingDateTime", 2);
const returnDate = parseDefaultDate(searchParams, "returnDateTime", 9);
const departureDateObject = convertToDate(departureDate, "dd/MM/yyyy", false);
const returnDateObject = convertToDate(returnDate, "dd/MM/yyyy", false);
const totalRooms = intParser(searchParams.get("numberOfRooms")) ?? 1;
const childAges = getChildAges(searchParams);
const totalAdults = intParser(searchParams.get("nb_adult")) ?? 2;

/*
nb_child is the total number of children AND infants
totalInfants is the number of children under age 2
totalChildren is the number of children age 2 or higher (nb_child - infants)
childAges is an array of the only of the children only (excluding infants) for selecting the age in the dropdowns
See https://aircanadavacations.atlassian.net/browse/ACVW-16250
*/

const nb_child = intParser(searchParams.get("nb_child")) ?? 0;
const totalChildren = childAges.length;
const totalInfants = nb_child - totalChildren;
const totalGuests = totalAdults + totalChildren + totalInfants;
const numberOfNights = nightBetweenDates(departureDateObject, returnDateObject);
const initialCabinClass = getDefaultCabinClass(searchParams);

const guestsValue = ref<GuestsData>({
  totalInfants,
  totalChildren,
  totalAdults,
  totalRooms,
  childAges,
  isDataValid: true, // assume data from URL is valid by default
});

const formData = computed(() => ({
  departureEntity,
  arrivalEntity,
  departureDate,
  returnDate,
  departureDateObject,
  returnDateObject,
  guests: guestsValue.value,
  totalGuests,
  numberOfNights,
  initialCabinClass,
}));

const getSearchUrl = async () => {
  const isB2b = await userStore.isB2BUser();
  if (isB2b) {
    const sirevsid = await userStore.getSirevSid();
    return queryStore.buildPackageSearchB2BUrl(sirevsid);
  }
  return queryStore.buildPackageSearchUrlParams();
};

async function submitPackageSearch() {
  const url = await getSearchUrl();
  window.open(url, "_self");
}

function emitUpdateMode() {
  emit("search-mode", ModifySearchMode.update);
}

function emitViewMode() {
  emit("search-mode", ModifySearchMode.view);
}
</script>

<template>
  <div
    class="search-content-panel"
    :id="tabConfig.id"
    role="tabpanel"
    :aria-labelledby="'btn-' + tabConfig.id"
  >
    <BaseSearchForm
      :tab-config="tabConfig"
      :product-type="productType"
      :form-data="formData"
      :is-packages="true"
      @modify="emitUpdateMode"
      @show-cuba-popup="emitViewMode"
      @submit="submitPackageSearch"
    />
  </div>
</template>
